<template>
  <div class="modal-overlay">
    <div class="modal-content-wrapper">
      <div class="modal-header">
        {{ titleText }}
        <CloseSvg
          class="close"
          :dark="true"
          @click="onCloseClicked"
        />
      </div>
      <div class="modal-content">
        <div class="edit-cartridge-flex-container-row">
          <TextInput
            v-model="name"
            :show-required="true"
            label="Name"
            class="first-item"
          />
          <NumericInput
            v-model="diameterMinimum"
            :show-required="true"
            label="Diameter Minimum"
            class="second-item"
          />
        </div>
      </div>
      <div class="modal-footer">
        <LockingButton
          class="save-button"
          :disabled="!isFormValid"
          :locked="saving"
          :text="'Save'"
          @click="onSaveClicked"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { Cartridge } from '@/@types/responses/cartridge/Cartridge'
import Toaster from '@meforma/vue-toaster'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { CaliberService } from '@/services/caliber.service'
import { CartridgeMessage } from '@/@types/messages/CartridgeMessage'
import { CartridgeService } from '@/services/cartridge.service'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'CartridgeModal',
  components: {
    CloseSvg,
    LockingButton,
    NumericInput,
    TextInput
  },
  props: {
    caliberId: {
      type: Number,
      required: true
    },
    cartridge: {
      type: Cartridge,
      required: true
    }
  },
  emits: ['cartridge-created', 'cartridge-updated', 'close'],
  setup() {
    const caliberService = inject(PROVIDER_CONSTANTS.caliberService) as CaliberService
    const cartridgeService = inject(PROVIDER_CONSTANTS.cartridgeService) as CartridgeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      caliberService,
      cartridgeService,
      toast
    }
  },
  data() {
    return {
      name: this.cartridge.name,
      diameterMinimum: this.cartridge.diameterMinimum,
      saving: false
    }
  },
  computed: {
    isFormValid(): boolean {
      return (this.name !== this.cartridge.name ||
             this.diameterMinimum !== this.cartridge.diameterMinimum) && (this.name.trim() !== '' && this.diameterMinimum >= 0)
    },
    titleText(): string {
      return this.cartridge.id === 0 ? 'Create Cartridge' : 'Edit Cartridge'
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onCloseClicked(): void {
      this.$emit('close')
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      this.saving = true

      const cartridgeMessage = new CartridgeMessage(this.diameterMinimum, this.name)
      if (this.cartridge.id === 0)
        this.caliberService.createCartridge(this.caliberId, cartridgeMessage).then(() => {
          this.$emit('cartridge-created')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the cartridge. Please try again.')
          }
        })
      else
        this.cartridgeService.updateCartridge(this.cartridge.id, cartridgeMessage).then(() => {
          this.$emit('cartridge-updated')
          this.$emit('close')
        }).catch(error => {
          this.saving = false
          
          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the cartridge. Please try again.')
          }
        })
    }
  }
})
</script>

<style scoped lang="scss">
.modal-overlay {
  @include modal-styles;
}

.modal-overlay > .modal-content-wrapper {
  > .modal-content > .edit-cartridge-flex-container-row {
    display: flex;
    flex-direction: column;

    @include desktop {
      flex-direction: row;
      margin-bottom: 15px;
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    > .first-item {
      margin-bottom: 15px;
      flex: 1;

      @include desktop {
        flex: 0 0 calc(50% - 10px);
        margin-right: 10px;
        margin-bottom: 0;
      }
    }

    > .second-item {
      margin-bottom: 15px;
      flex: 1;

      @include desktop {
        flex: 0 0 50%;
        margin-bottom: 0;
      }
    }
  }
}
</style>
